import { useOffreService } from '@europrocurement/l2d-domain';
import { OffresJsonldOffreRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { castIriToId } from '@europrocurement/l2d-utils';
import React, { createContext, PropsWithChildren, useContext, useEffect, useReducer } from 'react';
import { DataProviderProps, DataState } from './types';

export type offerVisibilityType = 'FULL' | 'PARTIAL';

export type Offre = {
    id: number;
    libelle: string;
    type: offerVisibilityType;
    icon: string | null;
    deleted: boolean;
    calculCombine: { id: number; category?: number } | null;
    calculMultiple: { id: number; category?: number } | null;
};

type OffresState = DataState & {
    data: Array<Offre>;
};

const INIT_OFFRES_CTX = 'init_offres_ctx';

type Action = { type: 'init_offres_ctx'; payload: Array<Offre> };

export const OffresContext = createContext<OffresState | null>(null);
export const OffresDispatchContext = createContext<React.Dispatch<Action> | null>(null);

export const offreApiToStateTransformer = (item: OffresJsonldOffreRead): Offre => ({
    id: item.id as number,
    libelle: item.libelle as string,
    type: item.visibilite,
    icon: item.iconeName ?? null,
    deleted: item.deleted ?? false,
    calculCombine: item.calculCombine
        ? {
              id: item.calculCombine.id as number,
              category: item.calculCombine.categorie
                  ? castIriToId(item.calculCombine.categorie)
                  : undefined,
          }
        : null,
    calculMultiple: item.calculMultiple
        ? {
              id: item.calculMultiple.id as number,
              category: item.calculMultiple.categorie
                  ? castIriToId(item.calculMultiple.categorie)
                  : undefined,
          }
        : null,
});

function offresReducer(_state: OffresState, action: Action): OffresState {
    switch (action.type) {
        case INIT_OFFRES_CTX:
            return { loaded: true, data: [...action.payload] };
        default:
            throw new Error(`Unknown action: ${(action as Action).type}`);
    }
}

export const OffresProvider: React.FunctionComponent<PropsWithChildren<DataProviderProps>> =
    function ({ autoload, children }) {
        const [state, dispatch] = useReducer(offresReducer, { loaded: false, data: [] });
        const { offersApi } = useOffreService();

        useEffect(() => {
            if (!autoload) {
                return;
            }

            offersApi
                .apiOffresGetCollection({ itemsPerPage: 999 })
                .then((response) => {
                    dispatch({
                        type: INIT_OFFRES_CTX,
                        payload: response.data['hydra:member'].map(offreApiToStateTransformer),
                    });
                })
                .catch((e) => {
                    console.error(e);
                    dispatch({ type: INIT_OFFRES_CTX, payload: [] });
                });
        }, [offersApi, autoload]);

        return (
            <OffresContext.Provider value={state}>
                <OffresDispatchContext.Provider value={dispatch}>
                    {children}
                </OffresDispatchContext.Provider>
            </OffresContext.Provider>
        );
    };

export function useOffresCtx() {
    const state = useContext(OffresContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getOffres = (): Array<Offre> => state?.data || [];

    const getOffreById = (id: number): Offre | undefined =>
        (state?.data || []).find((offre) => offre.id === id);

    return { isDataLoaded, getOffres, getOffreById };
}

export function useOffresCtxDispatch() {
    return useContext(OffresDispatchContext);
}
